import React from "react";
import styled from "styled-components";
import {motion} from 'framer-motion'

const Box = styled(motion.a)`
  width: 100%;
  max-width: 100%;
  height: 100%;
  text-decoration: none;
  padding: 0.85rem;
  box-sizing: border-box;
  overflow: hidden;
  color: ${(props) => props.theme.text};
  border: 2px solid ${(props) => props.theme.text};
  backdrop-filter: blur(2px);
  box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.2);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  z-index: 5;
  transition: transform 0.3s ease, background-color 0.3s ease, color 0.3s ease;

  /* Hover effects for desktop */
  @media (hover: hover) {
    &:hover {
      color: ${(props) => props.theme.body};
      background-color: ${(props) => props.theme.text};
      transform: translateY(-5px);
    }
  }
  
  /* Touch effects for mobile */
  @media (hover: none) {
    &:active {
      color: ${(props) => props.theme.body};
      background-color: ${(props) => props.theme.text};
      transform: translateY(-2px);
      transition: all 0.1s ease;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  flex-shrink: 0;
  height: 14rem;
  position: relative;
  overflow: hidden;
  margin-bottom: 0.5rem;
  border: 1px solid transparent;
  transition: border 0.3s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 1600px) {
    height: 16rem;
  }

  @media (max-width: 480px) {
    height: 12rem;
  }

  /* Hover effects for desktop */
  @media (hover: hover) {
    ${Box}:hover & {
      border: 1px solid ${(props) => props.theme.body};
    }
  }
  
  /* Touch effects for mobile */
  @media (hover: none) {
    ${Box}:active & {
      border: 1px solid ${(props) => props.theme.body};
    }
  }
`;

const CertImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  display: block;
  transition: transform 0.3s ease-in-out;
  
  /* Hover effects for desktop */
  @media (hover: hover) {
    ${Box}:hover & {
      transform: scale(1.05);
    }
  }
  
  /* Touch effects for mobile */
  @media (hover: none) {
    ${Box}:active & {
      transform: scale(1.02);
    }
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

const Title = styled.h3`
  color: inherit;
  padding: 0.25rem 0 0.2rem;
  flex-shrink: 0;
  font-family: "Karla", sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  line-height: 1.25;
  min-height: 3.75rem;
  max-height: 3.75rem;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  border-bottom: 1px solid ${(props) => props.theme.text};
  margin: 0;
  box-sizing: border-box;

  /* Hover effects for desktop */
  @media (hover: hover) {
    ${Box}:hover & {
      border-bottom: 1px solid ${(props) => props.theme.body};
    }
  }
  
  /* Touch effects for mobile */
  @media (hover: none) {
    ${Box}:active & {
      border-bottom: 1px solid ${(props) => props.theme.body};
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`

const HashTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 0.3rem 0.4rem;
  padding: 0.3rem 0 0;
  margin: 0;
  margin-top: auto;
  flex-shrink: 0;
  min-height: 3.25rem;
`

const Tag = styled.span`
  font-size: 0.85rem;
  line-height: 1.3;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    font-size: 0.78rem;
  }
`

const Container = styled(motion.div)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding: 0 0.5rem;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    padding: 0 0.25rem;
  }
`;

const Item={
  hidden:{
    scale:0
  },
  show:{
    scale:1,
    transition:{
      type:'spring',
      duration:0.5
    }
  }
}

const FeatsComponents = (props) => {
  const { name, tags, imgSrc, link } = props.blog;
  return (
    <Container variants={Item}>
      <Box href={link} target="_blank" rel="noopener noreferrer">
        <ImageContainer>
          <CertImage src={imgSrc} alt={name} loading="lazy" />
        </ImageContainer>
        <ContentContainer>
          <Title>{name}</Title>
          <HashTags>
            {tags.map((t, id) => (
              <Tag key={id}>#{t}</Tag>
            ))}
          </HashTags>
        </ContentContainer>
      </Box>
    </Container>
  );
};

export default FeatsComponents;
